import { observable, makeObservable, override } from 'mobx';
import isEqual from 'lodash/isEqual';

import { AbstractField } from './AbstractField';
import { store } from '@store';
import { translate } from '@chaika/i18n/helper';
import { assertIsDefined } from '@utils/assertIsDefined';

export interface SelectedData<VT = string> {
    value: VT;
    comment: string;
}

export class CheckboxWithTextListField extends AbstractField<SelectedData[], SelectedData[]> {
    @observable
    private options_?: Map<string, string>;

    constructor(params: SelectedData[], name: string) {
        super(params, name);
        makeObservable(this);
    }

    @override
    public get hasData(): boolean {
        return this.fieldValue.length > 0;
    }

    public makeUpdateParams() {
        return this.fieldValue;
    }

    public getPreview(): string {
        if (!this.options_) {
            this.setData();
        }
        const options = this.options_;
        assertIsDefined(options, 'Field options are built by setData above');

        return this.fieldValue
            .map(
                ({ value, comment }) =>
                    `${options.get(value) || value}${
                        comment ? ` ${translate('comment').toLowerCase()}: ${comment}` : ''
                    }`,
            )
            .join(', ');
    }

    public isEqual(value: SelectedData[]): boolean {
        return isEqual(this.fieldValue, value);
    }

    public setData() {
        const {
            common: { fieldsParams },
        } = store;
        const fieldParams = fieldsParams.get(this.fieldName);
        assertIsDefined(fieldParams, `Field params of ${this.fieldName} are loaded with the entry type`);
        const { data } = fieldParams;

        const options = new Map<string, string>();
        this.options_ = options;

        data?.options?.forEach(option => {
            options.set(option.value, option.label);
        });
    }

    protected setDefaultValueValue() {
        this.defaultNullValue = [];
    }

    protected setFieldValue(value: SelectedData[]) {
        this.fieldValue = value;
    }
}
